/**
 * Icon path definitions
 * These correspond to the SVG files in assets/svg/
 * Paths are extracted from the SVG files for inline rendering with dynamic styling
 */

export interface SvgElement {
  tag: 'path' | 'rect' | 'circle';
  attrs: Record<string, string>;
}

export interface IconDefinition {
  /** Single SVG path (simple icons) */
  path?: string;
  /** Multiple SVG elements (complex icons — use instead of path) */
  elements?: SvgElement[];
  strokeWidth?: number;
  viewBox?: string;
  /** If true, uses fill instead of stroke for single-path icons */
  fill?: boolean;
  /** If true, icon has its own colors and ignores the color prop */
  multiColor?: boolean;
  fillRule?: 'nonzero' | 'evenodd';
}

export const iconDefinitions: Record<string, IconDefinition> = {
  search: {
    path: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    strokeWidth: 2.5,
    viewBox: '0 0 24 24',
  },
  cross: {
    path: 'M6 18L18 6M6 6l12 12',
    strokeWidth: 2.5,
    viewBox: '0 0 24 24',
  },
  user: {
    path: 'M424.4,377.8c0,19.8-6,35.5-18.1,47c-12.1,11.5-28.1,17.2-48.1,17.2H141.5c-20,0-36-5.7-48.1-17.2  s-18.1-27.2-18.1-47c0-8.8,0.3-17.3,0.9-25.7c0.6-8.3,1.7-17.4,3.5-27c1.7-9.7,3.9-18.6,6.6-26.9s6.2-16.3,10.7-24.2  c4.5-7.9,9.6-14.5,15.4-20.1c5.8-5.5,12.9-10,21.2-13.3c8.3-3.3,17.6-5,27.6-5c1.5,0,5,1.8,10.4,5.3c5.5,3.6,11.6,7.5,18.5,11.9  c6.9,4.4,15.8,8.3,26.8,11.9c11,3.6,22,5.3,33.1,5.3s22.1-1.8,33.1-5.3c11-3.6,19.9-7.5,26.8-11.9c6.9-4.4,13-8.3,18.5-11.9  s8.9-5.3,10.4-5.3c10.1,0,19.3,1.7,27.6,5c8.3,3.3,15.4,7.7,21.2,13.3c5.8,5.5,10.9,12.2,15.4,20.1c4.5,7.9,8,15.9,10.7,24.2  s4.8,17.2,6.6,26.9c1.7,9.7,2.9,18.7,3.5,27C424.1,360.5,424.4,369.1,424.4,377.8z M345.1,156.4c0,26.3-9.3,48.7-27.9,67.3  s-41,27.9-67.3,27.9s-48.7-9.3-67.3-27.9s-27.9-41-27.9-67.3s9.3-48.7,27.9-67.3s41-27.9,67.3-27.9s48.7,9.3,67.3,27.9  S345.1,130.1,345.1,156.4z',
    viewBox: '0 0 500 500',
    fill: true,
  },
  logout: {
    path: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4m7 14l5-5-5-5m5 5H9',
    strokeWidth: 2,
    viewBox: '0 0 24 24',
  },
  support: {
    path: 'M725 1590 c-198 -31 -386 -141 -500 -291 -59 -78 -119 -203 -142 -295 -25 -103 -23 -277 5 -377 79 -281 289 -486 572 -558 82 -21 289 -18 365 5 242 73 397 200 504 413 53 106 75 203 75 333 0 141 -19 222 -79 345 -124 255 -366 414 -655 429 -47 3 -112 1 -145 -4z m242 -116 c41 -8 100 -26 130 -40 l55 -24 -87 -84 c-84 -80 -89 -83 -119 -75 -40 12 -182 12 -223 0 -30 -9 -35 -6 -112 71 -45 44 -81 83 -81 88 0 12 96 48 170 64 87 19 175 19 267 0z m8 -348 c67 -30 130 -95 164 -166 36 -76 36 -201 1 -275 -31 -64 -101 -134 -165 -165 -68 -34 -205 -35 -278 -2 -60 27 -136 103 -167 167 -35 74 -35 199 1 275 81 171 275 244 444 166z m-639 -77 l82 -81 -11 -37 c-13 -47 -13 -166 1 -214 l11 -39 -87 -84 c-97 -94 -89 -96 -129 19 -33 97 -42 253 -19 350 17 71 54 167 65 167 3 0 42 -37 87 -81z m1113 32 c62 -144 67 -330 14 -475 -16 -44 -33 -81 -39 -83 -5 -2 -46 32 -91 76 l-82 80 11 38 c13 45 13 176 0 218 -9 29 -5 34 72 113 44 45 83 82 87 82 4 0 16 -22 28 -49z m-681 -692 c48 -9 82 -8 139 1 l76 12 80 -80 c44 -44 77 -84 73 -90 -3 -6 -43 -23 -89 -39 -75 -25 -94 -28 -217 -27 -128 0 -140 2 -223 33 l-89 34 83 83 c46 46 88 84 93 84 5 0 38 -5 74 -11z',
    viewBox: '0 0 1670 1630',
    fill: true,
  },
  hamburger: {
    path: 'M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z',
    viewBox: '0 0 640 640',
    fill: true,
  },
  facebook: {
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l98.2 0 0-145.8-52.8 0 0-78.2 52.8 0 0-33.7c0-87.1 39.4-127.5 125-127.5 16.2 0 44.2 3.2 55.7 6.4l0 70.8c-6-.6-16.5-1-29.6-1-42 0-58.2 15.9-58.2 57.2l0 27.8 83.6 0-14.4 78.2-69.3 0 0 145.8 129 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32z',
    viewBox: '0 0 448 512',
    fill: true,
  },
  linkedin: {
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32zm5 170.2l66.5 0 0 213.8-66.5 0 0-213.8zm71.7-67.7a38.5 38.5 0 1 1 -77 0 38.5 38.5 0 1 1 77 0zM317.9 416l0-104c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9l0 105.8-66.4 0 0-213.8 63.7 0 0 29.2 .9 0c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9l0 117.2-66.4 0z',
    viewBox: '0 0 448 512',
    fill: true,
  },
  x: {
    path: 'M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32zm297.1 84l-103.8 118.6 122.1 161.4-95.6 0-74.8-97.9-85.7 97.9-47.5 0 111-126.9-117.1-153.1 98 0 67.7 89.5 78.2-89.5 47.5 0zM323.3 367.6l-169.9-224.7-28.3 0 171.8 224.7 26.4 0z',
    viewBox: '0 0 448 512',
    fill: true,
  },
  basket: {
    viewBox: '0 0 26 31',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M13.5 0C17.6421 0 21 3.35786 21 7.5C21 7.66806 20.9923 7.83469 20.9814 8H21.7373C23.2756 8 24.5539 9.15996 24.6689 10.6602L25.9922 27.9102C26.12 29.5779 24.7706 30.9999 23.0605 31H2.94043C1.23032 31 -0.11901 29.5779 0.00878906 27.9102L1.33105 10.6602C1.44609 9.16006 2.72457 8.00014 4.2627 8H6.01855C6.00767 7.83469 6 7.66806 6 7.5C6 3.3579 9.35791 5.58258e-05 13.5 0ZM13.5 2C10.4625 2.00006 8 4.46247 8 7.5C8 7.66857 8.00859 7.83529 8.02344 8H18.9766C18.9914 7.83529 19 7.66858 19 7.5C19 4.46243 16.5376 2 13.5 2Z',
          fill: '#35635A',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M17.7797 27C17.678 27 17.5254 27 17.4237 27C17.322 27 17.1695 27 17.0678 26.948L16.6102 26.8959V13.1041L17.0678 13.052C17.1695 13.052 17.2712 13.052 17.4237 13C17.678 13 17.8814 13 18.1864 13C18.339 13 18.4407 13 18.5424 13.052L19 13.1041V26.8959L18.5424 26.948C18.4407 26.948 18.339 26.948 18.1864 27C18.0847 27 17.9322 27 17.7797 27ZM8.16949 27C8.0678 27 7.91525 27 7.81356 27C7.71186 27 7.55932 27 7.45763 26.948L7 26.8959V13.1041L7.45763 13.052C7.55932 13.052 7.66102 13.052 7.81356 13C8.0678 13 8.27119 13 8.57627 13C8.72881 13 8.83051 13 8.9322 13.052L9.38983 13.1041V26.8959L8.9322 26.948C8.83051 26.948 8.72881 26.948 8.57627 27C8.42373 27 8.27119 27 8.16949 27ZM9.94915 19.0372C9.94915 18.9331 9.94915 18.881 9.94915 18.777C9.94915 18.7249 9.94915 18.6208 10 18.5688L10.0508 18.2565H15.8983L15.9492 18.5688C15.9492 18.6208 15.9492 18.7249 16 18.777C16 18.9331 16 19.0892 16 19.2454C16 19.3494 16 19.4015 15.9492 19.4535L15.8983 19.7658H10.1017L10.0508 19.4535C10.0508 19.4015 10.0508 19.2974 10 19.2454C10 19.1933 9.94915 19.0892 9.94915 19.0372ZM9.94915 21.223C9.94915 21.119 9.94915 21.0669 9.94915 20.9628C9.94915 20.9108 9.94915 20.8067 10 20.7546L10.0508 20.4424H15.8983L15.9492 20.7546C15.9492 20.8067 15.9492 20.9108 16 20.9628C16 21.119 16 21.2751 16 21.4312C16 21.5353 16 21.5874 15.9492 21.6394L15.8983 21.9517H10.1017L10 21.7435C10 21.6915 10 21.5874 9.94915 21.5353C10 21.4312 9.94915 21.3271 9.94915 21.223Z',
          fill: '#FFFFFF',
        },
      },
    ],
  },
  'lock-check': {
    path: 'M14.6818 7.46939H13.1364V5.07398C13.1364 2.27585 11.0568 0 8.5 0C5.94325 0 3.86364 2.2759 3.86364 5.07398V7.46939H2.31818C1.04028 7.46939 0 8.60785 0 10.0064V17.463C0 18.8615 1.04028 20 2.31818 20H14.6818C15.9597 20 17 18.8615 17 17.463V10.0064C17 8.60785 15.9597 7.46939 14.6818 7.46939ZM5.40909 5.07398C5.40909 3.2093 6.79518 1.69133 8.5 1.69133C10.2048 1.69133 11.5909 3.2093 11.5909 5.07398V7.46939H5.40909V5.07398ZM7.40755 17.1724L4.12822 13.5835L5.22067 12.389L7.40651 14.7812L11.7783 9.99782L12.8707 11.1934L7.40755 17.1724Z',
    viewBox: '0 0 17 20',
    fill: true,
  },
  visa: {
    viewBox: '0 0 49 31',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M2.48652 0H46.0007C47.374 0 48.4872 1.11036 48.4872 2.48V28.52C48.4872 29.8897 47.374 31 46.0007 31H2.48652C1.11328 31 0 29.8897 0 28.52V2.48C0 1.11036 1.11328 0 2.48652 0Z',
          fill: '#3A6DB7',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M17.3145 21.3261L19.3793 9.7752H22.6819L20.6156 21.3261H17.3145ZM32.5469 10.0242C31.8927 9.79019 30.8673 9.53906 29.5871 9.53906C26.3238 9.53906 24.0251 11.1057 24.0056 13.351C23.9871 15.0108 25.6466 15.9367 26.8992 16.4892C28.1848 17.0554 28.6168 17.4164 28.6108 17.9221C28.6027 18.6963 27.5842 19.05 26.635 19.05C25.3132 19.05 24.611 18.8749 23.5264 18.4437L23.1008 18.2602L22.6373 20.8459C23.4086 21.1684 24.8351 21.4477 26.316 21.4621C29.7876 21.4621 32.0411 19.9135 32.0668 17.5157C32.0792 16.2018 31.1994 15.2018 29.294 14.3774C28.1397 13.8431 27.4328 13.4865 27.4403 12.9454C27.4403 12.4653 28.0386 11.9518 29.3315 11.9518C30.4115 11.9359 31.1939 12.1604 31.8034 12.3944L32.0993 12.5277L32.5469 10.0242ZM41.0453 9.77502H38.4934C37.7028 9.77502 37.1112 9.98078 36.7641 10.7329L31.8594 21.3185H35.3273C35.3273 21.3185 35.8943 19.8951 36.0225 19.5826C36.4016 19.5826 39.7704 19.5876 40.2521 19.5876C40.3509 19.992 40.6538 21.3185 40.6538 21.3185H43.7183L41.0453 9.77467V9.77502ZM36.9965 17.2339C37.2695 16.5684 38.3123 14.0049 38.3123 14.0049C38.2927 14.0357 38.5833 13.3362 38.7501 12.9025L38.9732 13.8984C38.9732 13.8984 39.6057 16.6557 39.7379 17.2339H36.9965ZM14.5109 9.77502L11.2777 17.6522L10.9331 16.0514C10.3312 14.2061 8.45586 12.2069 6.35938 11.206L9.31573 21.3077L12.8099 21.3039L18.0091 9.77491H14.5109',
          fill: 'white',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M8.49776 9.5393H3.22145L3.17969 9.78253C7.28454 10.7502 10.0007 13.0886 11.1284 15.898L9.98105 10.526C9.78297 9.78581 9.20856 9.56492 8.49788 9.53906',
          fill: '#F2AE14',
        },
      },
    ],
  },
  mastercard: {
    viewBox: '0 0 48 31',
    multiColor: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M2.94727 1H45.0527C46.1834 1.00005 47 1.85166 47 2.78223V28.2178C47 29.1483 46.1834 29.9999 45.0527 30H2.94727C1.81665 29.9999 1 29.1483 1 28.2178V2.78223C1 1.85166 1.81665 1.00005 2.94727 1Z',
          fill: '#F3F3F3',
          stroke: '#F3F3F3',
          'stroke-width': '2',
        },
      },
      {
        tag: 'path',
        attrs: { d: 'M28.4706 22.9786H19.7539V7.42969H28.4706V22.9786Z', fill: '#FF5F00' },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M20.3135 15.2052C20.3135 12.051 21.8013 9.24131 24.1184 7.4307C22.424 6.10668 20.2856 5.31641 17.9616 5.31641C12.4599 5.31641 8 9.74371 8 15.2052C8 20.6666 12.4599 25.0939 17.9616 25.0939C20.2856 25.0939 22.424 24.3036 24.1184 22.9796C21.8013 21.169 20.3135 18.3593 20.3135 15.2052Z',
          fill: '#EB001B',
        },
      },
      {
        tag: 'path',
        attrs: {
          d: 'M40.2284 15.2052C40.2284 20.6666 35.7685 25.0939 30.2668 25.0939C27.9427 25.0939 25.8044 24.3036 24.1094 22.9796C26.427 21.169 27.9148 18.3593 27.9148 15.2052C27.9148 12.051 26.427 9.24131 24.1094 7.4307C25.8044 6.10668 27.9427 5.31641 30.2668 5.31641C35.7685 5.31641 40.2284 9.74371 40.2284 15.2052Z',
          fill: '#F79E1B',
        },
      },
    ],
  },
  'delete-circle': {
    viewBox: '0 0 19 19',
    fill: true,
    elements: [
      {
        tag: 'path',
        attrs: {
          d: 'M9.38442 0C7.52836 0 5.71398 0.550386 4.17072 1.58156C2.62746 2.61273 1.42463 4.07838 0.71435 5.79315C0.00406611 7.50793 -0.181777 9.39482 0.180323 11.2152C0.542423 13.0356 1.4362 14.7078 2.74864 16.0202C4.06107 17.3326 5.73321 18.2264 7.55361 18.5885C9.37401 18.9506 11.2609 18.7648 12.9757 18.0545C14.6905 17.3442 16.1561 16.1414 17.1873 14.5981C18.2184 13.0549 18.7688 11.2405 18.7688 9.38441C18.7659 6.89641 17.7762 4.51116 16.017 2.75187C14.2577 0.99259 11.8724 0.00293516 9.38442 0ZM9.38442 17.0626C7.86582 17.0626 6.38133 16.6123 5.11866 15.7686C3.856 14.9249 2.87187 13.7257 2.29073 12.3227C1.70958 10.9197 1.55753 9.3759 1.85379 7.88648C2.15006 6.39706 2.88133 5.02895 3.95514 3.95514C5.02895 2.88133 6.39707 2.15005 7.88648 1.85379C9.3759 1.55753 10.9197 1.70958 12.3227 2.29072C13.7257 2.87186 14.9249 3.85599 15.7686 5.11866C16.6123 6.38132 17.0626 7.86582 17.0626 9.38441C17.0601 11.42 16.2503 13.3715 14.811 14.8109C13.3716 16.2503 11.42 17.0601 9.38442 17.0626Z',
        },
      },
      {
        tag: 'rect',
        attrs: {
          x: '6.71484',
          y: '5.51562',
          width: '9.24416',
          height: '1.69966',
          rx: '0.84983',
          transform: 'rotate(45 6.71484 5.51562)',
        },
      },
      {
        tag: 'rect',
        attrs: {
          x: '13.2539',
          y: '6.71875',
          width: '9.24416',
          height: '1.69966',
          rx: '0.84983',
          transform: 'rotate(135 13.2539 6.71875)',
        },
      },
    ],
  },
  'eye-open': {
    path: 'M17.7469 15.4149C17.9855 14.8742 18.1188 14.2724 18.1188 14.0016C18.1188 11.6544 16.2952 9.7513 14.046 9.7513C11.7969 9.7513 9.97332 11.6544 9.97332 14.0016C9.97332 16.3487 12.0097 17.8886 14.046 17.8886C15.3486 17.8886 16.508 17.2515 17.2517 16.2595C17.4466 16.0001 17.6137 15.7168 17.7469 15.4149ZM14.046 15.7635C14.5551 15.7635 15.0205 15.5684 15.3784 15.2457C15.81 14.8566 16 14.2807 16 14.0016C16 12.828 15.1716 11.8764 14.046 11.8764C12.9205 11.8764 12 12.8264 12 14C12 14.8104 12.9205 15.7635 14.046 15.7635Z M1.09212 14.2724C1.07621 14.2527 1.10803 14.2931 1.09212 14.2724C0.96764 14.1021 0.970773 13.8996 1.09268 13.7273C1.10161 13.7147 1.11071 13.7016 1.11993 13.6882C4.781 8.34319 9.32105 5.5 14.0142 5.5C18.7025 5.5 23.2385 8.33554 26.8956 13.6698C26.965 13.771 27 13.875 27 13.9995C27 14.1301 26.9593 14.2399 26.8863 14.3461C23.2302 19.6702 18.6982 22.5 14.0142 22.5C9.30912 22.5 4.75717 19.6433 1.09212 14.2724ZM3.93909 13.3525C3.6381 13.7267 3.6381 14.2722 3.93908 14.6465C7.07417 18.5443 10.6042 20.3749 14.0142 20.3749C17.4243 20.3749 20.9543 18.5443 24.0894 14.6465C24.3904 14.2722 24.3904 13.7267 24.0894 13.3525C20.9543 9.45475 17.4243 7.62513 14.0142 7.62513C10.6042 7.62513 7.07417 9.45475 3.93909 13.3525Z',
    viewBox: '0 0 28 28',
    fill: true,
    fillRule: 'evenodd',
  },
  'eye-closed': {
    path: 'M22.6928 1.55018C22.3102 1.32626 21.8209 1.45915 21.6 1.84698L19.1533 6.14375C17.4864 5.36351 15.7609 4.96457 14.0142 4.96457C9.32104 4.96457 4.781 7.84644 1.11993 13.2641L1.10541 13.2854L1.09271 13.3038C0.970762 13.4784 0.967649 13.6837 1.0921 13.8563C3.79364 17.8691 6.97705 20.4972 10.3484 21.6018L8.39935 25.0222C8.1784 25.4101 8.30951 25.906 8.69214 26.1299L9.03857 26.3326C9.4212 26.5565 9.91046 26.4237 10.1314 26.0358L23.332 2.86058C23.553 2.47275 23.4219 1.97684 23.0392 1.75291L22.6928 1.55018ZM18.092 8.00705C16.7353 7.40974 15.3654 7.1186 14.0142 7.1186C10.6042 7.1186 7.07416 8.97311 3.93908 12.9239C3.63812 13.3032 3.63812 13.8561 3.93908 14.2354C6.28912 17.197 8.86102 18.9811 11.438 19.689L12.7855 17.3232C11.2462 16.8322 9.97333 15.4627 9.97333 13.5818C9.97333 11.2026 11.7969 9.27368 14.046 9.27368C15.0842 9.27368 16.0317 9.68468 16.7511 10.3612L18.092 8.00705ZM15.639 12.3137C15.2926 11.7767 14.7231 11.4277 14.046 11.4277C12.9205 11.4277 12 12.3906 12 13.5802C12 14.3664 12.8432 15.2851 13.9024 15.3624L15.639 12.3137Z M14.6873 22.1761C19.1311 21.9148 23.4056 19.0687 26.8864 13.931C26.9593 13.8234 27 13.7121 27 13.5797C27 13.4535 26.965 13.3481 26.8956 13.2455C25.5579 11.2677 24.1025 9.62885 22.5652 8.34557L21.506 10.2052C22.3887 10.9653 23.2531 11.87 24.0894 12.9239C24.3904 13.3032 24.3904 13.8561 24.0894 14.2354C21.5676 17.4135 18.7903 19.2357 16.0254 19.827L14.6873 22.1761Z',
    viewBox: '0 0 28 28',
    fill: true,
    fillRule: 'evenodd',
  },
};
